import { Component, Element, Prop, h, Host } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-hero-header-type-7';

/**
 * Single-column centered dark hero header for SignNow functionality pages.
 * Simplified variant of sn-hero-header-type-3: same dark-blue background, no breadcrumbs.
 * Features H1 heading, subtitle description, free-trial form, optional sign-link below the form,
 * and optional G2 awards/badges block (mutually exclusive with sign-link in practice).
 * Used on SignNow functionality inner pages (e.g. /functionality/convert-powerpoint-to-pdf).
 *
 * @product signnow
 * @source hero-header
 * @version 1.1.0
 * @category SignNow
 * @contentConfig Single-column dark hero with heading, description, free-trial form, optional sign-link or awards/badges
 * @slot heading - {required}, <me-heading tag="h1" variant="1"> - Main H1 section heading
 * @slot description - <me-text tag="p"> - Subtitle paragraph below the heading
 * @slot action-primary - <form-free-trial-type-1> - Free-trial email form
 * @slot sign-link - {multiple}, <me-text tag="p">, <me-button tag="a" variant="ghost" icon="arrows--right-arrow-alt" icon-position="after"> - Optional label text + outgoing white link below the form
 * @example
 *   <sn-hero-header-type-7>
 *     <me-heading slot="heading" tag="h1" variant="1">Convert PowerPoint to PDF</me-heading>
 *     <me-text slot="description" tag="p">signNow offers a broad set of PDF editing tools and a straightforward interface. Get the most out of this robust solution for online document management at the most attractive prices.</me-text>
 *     <form-free-trial-type-1 slot="action-primary" button-variant="primary" mobile-layout="stacked" note="No credit card required" submit-text="Start free trial"></form-free-trial-type-1>
 *     <me-text slot="sign-link" tag="p">See how it works!</me-text>
 *     <me-button slot="sign-link" tag="a" href="https://www.signnow.com/samples" variant="ghost" icon="arrows--right-arrow-alt" icon-position="after" icon-lazy="false">Click here to sign a sample doc</me-button>
 *   </sn-hero-header-type-7>
 */
@Component({
  tag: 'sn-hero-header-type-7',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNHeroHeaderType7 {
  @Element() el: HTMLElement;

  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Awards section heading text.
   * @content
   * @type {string}
   */
  @Prop() awardsHeading?: string = 'signNow regularly wins awards for ease of use and setup';

  /**
   * G2 award badge images displayed below the form.
   * @type {array}
   * @item.type {object}
   * @item.property {string} src - Badge image URL
   * @item.property {string} alt - Badge alt text for accessibility
   * @item.property {string} link - URL the badge links to
   * @item.property {string} title - Tooltip / aria-label for the link
   * @item.required src
   * @item.required alt
   * @minItems 4
   */
  @Prop({ mutable: true }) badges?: { src: string; alt: string; link?: string; title?: string }[] | string;

  componentWillLoad() {
    this.badges = parseJsonProp(this.badges);
  }

  render() {
    const badges = Array.isArray(this.badges) ? this.badges : [];

    return (
      <Host data-color-mode={this.colorMode}>
        <section class={COMPONENT_NAME} aria-label="Hero header">
          <div class={`${COMPONENT_NAME}__container`}>
            <div class={`${COMPONENT_NAME}__content`}>
              <slot name="heading"></slot>
              <div class={`${COMPONENT_NAME}__description`}>
                <slot name="description"></slot>
              </div>
              <div class={`${COMPONENT_NAME}__action`}>
                <slot name="action-primary"></slot>
              </div>
              <div class={`${COMPONENT_NAME}__sign-link`}>
                <slot name="sign-link"></slot>
              </div>
              {badges.length > 0 && (
                <div class={`${COMPONENT_NAME}__awards`}>
                  {this.awardsHeading && (
                    <h2 class={`${COMPONENT_NAME}__awards-heading`}>{this.awardsHeading}</h2>
                  )}
                  <div class={`${COMPONENT_NAME}__badges`}>
                    {badges.map(badge =>
                      badge.link ? (
                        <a
                          class={`${COMPONENT_NAME}__badge-link`}
                          href={badge.link}
                          title={badge.title}
                          aria-label={badge.title ?? badge.alt}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                        >
                          <img
                            class={`${COMPONENT_NAME}__badge-img`}
                            src={badge.src}
                            alt={badge.alt}
                            width="112"
                            height="129"
                            loading="lazy"
                            decoding="async"
                          />
                        </a>
                      ) : (
                        <img
                          class={`${COMPONENT_NAME}__badge-img`}
                          src={badge.src}
                          alt={badge.alt}
                          width="112"
                          height="129"
                          loading="lazy"
                          decoding="async"
                        />
                      ),
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </Host>
    );
  }
}
